import React, { useEffect, useMemo, useState } from "react";

import {
  Header,
  Table,
  Button,
  Dropdown,
  Modal,
  Input,
  Pagination,
  Icon,
  TextArea,
} from "semantic-ui-react";

import { Calendar, momentLocalizer } from "react-big-calendar";
import { format } from "date-fns";

import moment from "moment";

import DateTextBox from "../common/DateTextBox";

import "./CardPhysiotherapistList.scss";

import { parseBeOrAdDate, formatThaiDate } from "react-lib/utils/dateUtils";

type CardPhysiotherapistListProps = {
  buttonLoadCheck?: any;
  onEvent: any;
  currentTherapists?: any;
  educationList?: any[];
  specialtyList?: any[];
  clinicalTermSet?: any;
  employeeDocuments?: any;
  doctorTherapyDetail?: any;
  doctorList?: any[];
  doctorTherapyItemDetail?: any;
  doctorTherapyList?: any[];
  schedulingList?: any[];
  auditLogList?: any[];
};

type PhysioRequest = {
  employee: any;
  actions: any;
  owner: any;
};

const localizer = momentLocalizer(moment);

const ICON = {
  IconUser: "/static/images/icon_user.png",
  IconRejectReason: "/static/images/icon_reject_reason.png",
  IconReject: "/static/images/icon_reject.png",
  IconEditHistory: "/static/images/icon_edit_history.png",
  IconEdit: "/static/images/icon_edit.png",
  IconBankPreview: "/static/images/icon_bank_preview.png",
  IconTopic: "/static/images/icon_topic.png",
  IconService: "/static/images/icon_therapy_service.png",
  IconTopicLightPink: "/static/images/icon_topic_light_pink.png",
};

const COLOR = {
  primary: "var(--primary-theme-color)",
  gray: "#757575",
  ccc_gray: "#ccc",
  light_gray: "#f5f5f5",
  light_pink: "#ffd8d8",
  light_red: "#f24646",
  red: "#d31919",
  green: "#14ae5c",
  dark_orange: "#d06500",
};

const STATUS_OPTIONS = [
  { key: "approve", text: "เปิดให้บริการ", value: "A" },
  { key: "suspended", text: "ถูกระงับชั่วคราว", value: "SU" },
  { key: "discontinued", text: "ยกเลิกให้บริการ", value: "D" },
  { key: "all", text: "ทั้งหมด", value: "all" },
];

const ROWS_PER_PAGE = 15;

const EXCLUDE_FIELDS = [
  "edited",
  "first_name",
  "first_name_en",
  "last_name",
  "last_name_en",
  "name_code",
  "code",
];

const EDIT_DATA_MAPPING: Record<string, { main: string; sub: string }> = {
  // ข้อมูลส่วนตัว
  full_name: { main: "ข้อมูลส่วนตัว", sub: "ชื่อ-นามสกุล" },
  first_name: { main: "ข้อมูลส่วนตัว", sub: "ชื่อ (ไทย)" },
  last_name: { main: "ข้อมูลส่วนตัว", sub: "นามสกุล (ไทย)" },
  first_name_en: { main: "ข้อมูลส่วนตัว", sub: "ชื่อ (อังกฤษ)" },
  last_name_en: { main: "ข้อมูลส่วนตัว", sub: "นามสกุล (อังกฤษ)" },
  email: { main: "ข้อมูลส่วนตัว", sub: "อีเมล" },
  phone_no: { main: "ข้อมูลส่วนตัว", sub: "เบอร์โทรศัพท์" },
  name_code: { main: "ข้อมูลส่วนตัว", sub: "ชื่อโค้ด" },
  birth_date: { main: "ข้อมูลส่วนตัว", sub: "วันเดือนปีเกิด" },
  citizen_no: { main: "ข้อมูลส่วนตัว", sub: "เลขบัตรประชาชน" },
  gender: { main: "ข้อมูลส่วนตัว", sub: "เพศ" },

  // ข้อมูลทางวิชาชีพ
  licence: { main: "ข้อมูลทางวิชาชีพ", sub: "เลขที่ใบประกอบวิชาชีพ" },
  work_address: { main: "ข้อมูลทางวิชาชีพ", sub: "สถานที่ทำงาน" },
  specialties: { main: "ข้อมูลทางวิชาชีพ", sub: "ความเชี่ยวชาญและกลุ่มอาการ" },
};

const lightPinkBoxStyle: React.CSSProperties = {
  backgroundColor: COLOR.light_pink,
  borderRadius: "8px",
  padding: "0.3rem 0.8rem",
  fontSize: "16px",
  whiteSpace: "nowrap",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  height: "28.38px",
};

const CardPhysiotherapistList: React.FC<CardPhysiotherapistListProps> = (props) => {
  const today = new Date().toISOString().split("T")[0];

  const [filterName, setFilterName] = useState("");
  const [filterStartDate, setFilterStartDate] = useState(today);
  const [filterEndDate, setFilterEndDate] = useState(today);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filteredTherapists, setFilteredTherapists] = useState<any[]>([]);
  const [selectedRequest, setSelectedRequest] = useState<PhysioRequest | null>(null);
  const [activePage, setActivePage] = useState(1);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [isRejectReasonModalOpen, setIsRejectReasonModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectTarget, setRejectTarget] = useState<any>(null);
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [previewUrl, setPreviewUrl] = React.useState("");
  const [zoom, setZoom] = useState(1);
  const [isEditHistoryOpen, setIsEditHistoryOpen] = useState(false);
  const [isRejectLoading, setRejectLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [doctorTherapyItemDetailList, setDoctorTherapyItemDetailList] = useState<any[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<any>(null);
  const [isSearching, setIsSearching] = useState(false);

  const thaiMonths = [
    "",
    "ม.ค.",
    "ก.พ.",
    "มี.ค.",
    "เม.ย.",
    "พ.ค.",
    "มิ.ย.",
    "ก.ค.",
    "ส.ค.",
    "ก.ย.",
    "ต.ค.",
    "พ.ย.",
    "ธ.ค.",
  ];

  const jobTypeList = [
    { key: "fulltime", text: "งานประจำ", value: "fulltime" },
    { key: "parttime", text: "งานพาร์ทไทม์", value: "parttime" },
    { key: "intern", text: "นักศึกษาฝึกงาน", value: "intern" },
    { key: "contract", text: "งานสัญญาจ้าง", value: "contract" },
    { key: "freelance", text: "ฟรีแลนซ์", value: "freelance" },
  ];

  const jobLevelList = [
    { key: "entry", text: "ระดับเริ่มต้น", value: "entry" },
    { key: "junior", text: "จูเนียร์", value: "junior" },
    { key: "mid", text: "ระดับกลาง", value: "mid" },
    { key: "senior", text: "ซีเนียร์", value: "senior" },
    { key: "manager", text: "ผู้จัดการ", value: "manager" },
    { key: "director", text: "ผู้อำนวยการ", value: "director" },
  ];

  const getDisplayName = (employee: any) =>
    employee?.full_name?.trim() ||
    `${employee?.first_name ?? ""} ${employee?.last_name ?? ""}`.trim();

  const pendingTherapists = (props.currentTherapists || [])
    .filter((t: any) => t.status === "S")
    .filter((t: any) => {
      if (!filterName) return true;
      return getDisplayName(t.employee).toLowerCase().includes(filterName.toLowerCase());
    })
    .filter((t: any) =>
      filterStatus && filterStatus !== "all" ? t.status === filterStatus : true
    );

  const totalPages = Math.max(1, Math.ceil(pendingTherapists.length / ROWS_PER_PAGE));

  const handlePageChange = (_e: any, { activePage }: any) => {
    setActivePage(activePage);
  };

  const handlePreviewDocument = (type: string) => {
    const doc = props.employeeDocuments.find((d: any) => d.type === type);
    if (!doc) return alert("เอกสารไม่พบ");
    setPreviewUrl(doc.file);
    setPreviewOpen(true);
  };

  const currentData = filteredTherapists.slice(
    (activePage - 1) * ROWS_PER_PAGE,
    activePage * ROWS_PER_PAGE
  );

  const emptyRowsCount = ROWS_PER_PAGE - currentData.length;
  const emptyRows = Array.from({ length: emptyRowsCount > 0 ? emptyRowsCount : 0 });

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "-";
    return moment(dateStr).format("DD/MM/YYYY");
  };

  const getSchoolName = (schoolCode: string) => {
    const eduItems = props.clinicalTermSet?.edu_institution?.items || [];
    const found = eduItems.find((item: any) => item.code === schoolCode);
    return found ? found.name : schoolCode || "-";
  };

  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.2, 3));
  const handleZoomOut = () => setZoom((prev) => Math.max(prev - 0.1, 0.2));

  const handleSearchClick = () => {
    if (!props.currentTherapists) return;

    setIsSearching(true);

    setTimeout(() => {
      const formatDate = (dateStr: string, isEnd = false) => {
        if (!dateStr) return undefined;
        const date = new Date(dateStr);
        if (isEnd) date.setHours(23, 59, 59, 999);
        return date.getTime();
      };

      const startTime = formatDate(filterStartDate);
      const endTime = formatDate(filterEndDate, true);

      let filtered = props.currentTherapists.filter((t: any) => t.status === "A");

      if (filterName) {
        const lowerName = filterName.toLowerCase();
        filtered = filtered.filter((t: any) => {
          const fullName = `${t.employee?.first_name ?? ""} ${t.employee?.last_name ?? ""}`.trim();
          return fullName.toLowerCase().includes(lowerName);
        });
      }

      if (filterStatus && filterStatus !== "all") {
        filtered = filtered.filter((t: any) => t.status === filterStatus);
      }

      if (startTime) {
        filtered = filtered.filter(
          (t: any) => t.approve_at && new Date(t.approve_at).getTime() >= startTime
        );
      }
      if (endTime) {
        filtered = filtered.filter(
          (t: any) => t.approve_at && new Date(t.approve_at).getTime() <= endTime
        );
      }

      setFilteredTherapists(filtered);
      setIsSearching(false);
    }, 300);
  };

  const FilterDate = ({
    value,
    onChange,
    placeholder = "dd/mm/yyyy",
  }: {
    value: string;
    onChange: (val: string) => void;
    placeholder?: string;
  }) => {
    const [localValue, setLocalValue] = React.useState(
      value ? moment(value).add(543, "years").format("DD/MM/YYYY") : ""
    );

    React.useEffect(() => {
      setLocalValue(value ? moment(value).add(543, "years").format("DD/MM/YYYY") : "");
    }, [value]);

    return (
      <DateTextBox
        placeholder={placeholder}
        value={localValue}
        onChange={(val) => {
          setLocalValue(val);

          const isFullDate = /^\d{2}\/\d{2}\/\d{4}$/.test(val);
          if (!isFullDate) return;

          const date = parseBeOrAdDate(val);
          if (date && date.isValid()) {
            onChange(date.format("YYYY-MM-DD"));
          }
        }}
      />
    );
  };

  const actions = selectedRequest?.actions || [];
  const rejectActions = actions.filter((a: any) => a.action === "REJECT");
  const latestReject = rejectActions.length > 0 ? rejectActions[rejectActions.length - 1] : null;

  const CustomTabs = ({ tabs, onToggleEditHistory }: any) => {
    return (
      <div className="trapezoid-tabs-container">
        <nav className="trapezoid-nav">
          {tabs.map((tab: any, idx: any) => (
            <button
              type="button"
              key={idx}
              className={`tab ${activeIndex === idx ? "selected" : ""}`}
              onClick={() => setActiveIndex(idx)}
            >
              {tab.label}
            </button>
          ))}
        </nav>

        {tabs[activeIndex].label !== "ตารางการลงตรวจ" && (
          <div style={{ marginTop: "20px", textAlign: "right", marginRight: "10px" }}>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                color: COLOR.dark_orange,
                fontWeight: "bold",
                borderBottom: `2px solid ${COLOR.dark_orange}`,
                cursor: "pointer",
                marginRight: "5px",
              }}
              onClick={onToggleEditHistory}
            >
              <img
                src={ICON.IconEditHistory}
                alt="Edit History"
                style={{ width: "14px", height: "14px", marginRight: "5px" }}
              />
              ดูประวัติการแก้ไขข้อมูล
            </div>
          </div>
        )}

        <div style={{ overflowY: "auto", height: "50vh", marginBottom: "50px" }}>
          {tabs[activeIndex].content}
        </div>
      </div>
    );
  };

  const getJobTypeText = (value: any) => {
    const found = jobTypeList.find((item) => item.value === value);
    return found ? found.text : value;
  };

  const getJobLevelText = (value: any) => {
    const found = jobLevelList.find((item) => item.value === value);
    return found ? found.text : value;
  };

  const getEducationLevelName = (code: string) => {
    const item = props.educationList?.find((opt) => opt.code === code);
    return item ? item.name : code;
  };

  const getEducationStatusName = (status: string) => {
    switch (status) {
      case "graduated":
        return "สำเร็จการศึกษา";
      case "studying":
        return "กำลังศึกษา";
      default:
        return status || "-";
    }
  };

  const getCategoryName = (categoryCode: string) => {
    const eduItems = props.clinicalTermSet?.edu_category?.items || [];
    const found = eduItems.find((item: any) => item.code === categoryCode);
    return found ? found.name : categoryCode || "-";
  };

  const renderPersonalDetail = (selectedRequest: any, setSelectedRequest: any) => {
    const specialtyNames =
      selectedRequest.employee?.extra?.specialties
        ?.map((code: string) => {
          return props.specialtyList?.find((s: any) => s.code === code)?.name || code;
        })
        .join(", ") || "-";

    const panes = [
      {
        label: "ข้อมูลทั่วไป",
        content: (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "2rem",
                marginTop: "40px",
                marginLeft: "20px",
              }}
            >
              <div>
                <div style={{ minHeight: "200px" }}>
                  <h4 style={{ color: COLOR.primary }}>ข้อมูลส่วนตัว</h4>
                  {[
                    {
                      label: "ชื่อ-นามสกุล",
                      value:
                        selectedRequest.employee?.full_name?.trim() ||
                        `${selectedRequest.employee?.first_name ?? ""} ${
                          selectedRequest.employee?.last_name ?? ""
                        }`.trim(),
                    },
                    {
                      label: "วัน/เดือน/ปีเกิด",
                      value: formatDate(selectedRequest.employee?.birth_date),
                    },
                    { label: "เลขที่บัตรประชาชน", value: selectedRequest.employee?.citizen_no },
                    {
                      label: "เพศ",
                      value: selectedRequest.employee?.gender === "M" ? "Male" : "Female",
                    },
                    { label: "อีเมล์", value: selectedRequest.employee?.email },
                    { label: "เบอร์โทร", value: selectedRequest.employee?.phone_no },
                  ].map((item, idx) => (
                    <div key={idx} style={{ display: "flex", marginBottom: "0.5rem" }}>
                      <div style={{ width: "150px", fontWeight: "bold", marginLeft: "10px" }}>
                        {item.label}
                      </div>
                      <div>{item.value || "-"}</div>
                    </div>
                  ))}
                </div>

                <div style={{ minHeight: "200px" }}>
                  <h4 style={{ color: COLOR.primary, marginTop: "2rem" }}>ประวัติการศึกษา</h4>
                  {selectedRequest.employee?.extra?.educations?.map((edu: any, idx: number) => {
                    const educationLevelName =
                      props.educationList?.find((item: any) => item.code === edu.level)?.name ||
                      "-";
                    return (
                      <div key={idx} style={{ display: "flex", position: "relative" }}>
                        <div style={{ marginLeft: "10px", paddingRight: "5px" }}>
                          <div style={{ fontWeight: "bold" }}>ลำดับที่ {idx + 1}</div>
                        </div>
                        <div
                          style={{
                            width: "40px",
                            display: "flex",
                            justifyContent: "center",
                            position: "relative",
                          }}
                        >
                          <div
                            style={{
                              width: "10px",
                              height: "10px",
                              borderRadius: "50%",
                              backgroundColor: COLOR.primary,
                              zIndex: 1,
                            }}
                          ></div>
                          <div
                            style={{
                              position: "absolute",
                              top: 0,
                              bottom: 0,
                              width: "2px",
                              borderLeft: `2px dotted ${COLOR.primary}`,
                              margin: "0 auto",
                            }}
                          ></div>
                        </div>
                        <div style={{ flex: 1, paddingLeft: "5px" }}>
                          <div style={{ display: "flex", marginBottom: "0.3rem" }}>
                            <strong style={{ width: "130px" }}>ระดับการศึกษา</strong>
                            <div>{educationLevelName}</div>
                          </div>
                          <div style={{ display: "flex", marginBottom: "0.3rem" }}>
                            <strong style={{ width: "130px" }}>สถาบันการศึกษา</strong>
                            <div>{getSchoolName(edu.school) || "-"}</div>
                          </div>
                          <div style={{ display: "flex", marginBottom: "0.3rem" }}>
                            <strong style={{ width: "130px" }}>คณะ</strong>
                            <div>{edu.faculty || "-"}</div>
                          </div>
                          <div style={{ display: "flex", marginBottom: "0.3rem" }}>
                            <strong style={{ width: "130px" }}>สาขา</strong>
                            <div>{edu.major || "-"}</div>
                          </div>
                          <div style={{ display: "flex", marginBottom: "1.3rem" }}>
                            <strong style={{ width: "130px" }}>ปีที่สำเร็จ</strong>
                            <div>{edu.graduate_year || "-"}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div>
                <div style={{ minHeight: "200px" }}>
                  <h4 style={{ color: COLOR.primary }}>ข้อมูลทางวิชาชีพ</h4>
                  <div style={{ marginBottom: "1rem", marginLeft: "10px" }}>
                    <div style={{ display: "flex", marginBottom: "0.3rem" }}>
                      <div style={{ width: "180px", fontWeight: "bold" }}>
                        เลขที่ใบประกอบวิชาชีพ
                      </div>
                      <div>{selectedRequest.employee?.licence || "-"}</div>
                    </div>
                    <div style={{ display: "flex", marginBottom: "0.3rem" }}>
                      <div style={{ width: "180px", fontWeight: "bold" }}>ความเชี่ยวชาญ</div>
                      <div style={{ minWidth: "300px", width: "50%" }}>{specialtyNames}</div>
                    </div>
                    <div style={{ display: "flex", marginBottom: "0.3rem" }}>
                      <div style={{ width: "180px", fontWeight: "bold" }}>เอกสารที่เกี่ยวข้อง</div>
                      <div style={{ display: "flex", gap: "1rem", marginTop: "0.5rem" }}>
                        <Button
                          onClick={() => handlePreviewDocument("LICENCE")}
                          style={{ backgroundColor: COLOR.light_pink, color: "black" }}
                        >
                          กดเพื่อดูใบประกอบวิชาชีพ
                        </Button>
                        <Button
                          onClick={() => handlePreviewDocument("CITIZEN_ID")}
                          style={{ backgroundColor: COLOR.light_pink, color: "black" }}
                        >
                          กดเพื่อดูบัตรประชาชน
                        </Button>
                      </div>
                    </div>
                    <Modal
                      open={previewOpen}
                      onClose={() => {
                        setPreviewOpen(false);
                        setZoom(1);
                      }}
                      size="small"
                    >
                      <Modal.Content>
                        <div
                          style={{ display: "flex", justifyContent: "right", alignItems: "center" }}
                        >
                          <div
                            style={{
                              display: "flex",
                              gap: "0.2rem",
                              backgroundColor: "#e0e0e0",
                              padding: "0.1rem",
                              borderRadius: "8px",
                              alignItems: "center",
                            }}
                          >
                            <Button
                              icon
                              onClick={handleZoomOut}
                              size="tiny"
                              style={{
                                backgroundColor: "white",
                                borderRadius: "50%",
                                padding: "0.1rem",
                              }}
                            >
                              <Icon name="minus" />
                            </Button>
                            <Button
                              icon
                              onClick={handleZoomIn}
                              size="tiny"
                              style={{
                                backgroundColor: "white",
                                borderRadius: "50%",
                                padding: "0.1rem",
                              }}
                            >
                              <Icon name="plus" />
                            </Button>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "flex-start",
                                justifyContent: "center",
                                paddingBottom: "0.2rem",
                              }}
                            >
                              <Icon name="zoom" />
                            </div>
                          </div>

                          <Button
                            icon
                            onClick={() => {
                              setPreviewOpen(false);
                              setZoom(1);
                            }}
                            size="large"
                            style={{
                              backgroundColor: "white",
                              color: "black",
                            }}
                          >
                            <Icon name="close" />
                          </Button>
                        </div>
                        {previewUrl ? (
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              height: "70vh",
                              overflow: "auto",
                            }}
                          >
                            <img
                              src={previewUrl}
                              alt="document preview"
                              style={{
                                maxWidth: "100%",
                                maxHeight: "100%",
                                objectFit: "contain",
                                transform: `scale(${zoom})`,
                                transformOrigin: "top left",
                                transition: "transform 0.2s",
                              }}
                            />
                          </div>
                        ) : (
                          <p>เอกสารไม่พบ</p>
                        )}
                      </Modal.Content>
                    </Modal>
                  </div>
                </div>

                <div style={{ minHeight: "200px" }}>
                  <h4 style={{ color: COLOR.primary, marginTop: "2rem" }}>ประวัติการทำงาน</h4>
                  {selectedRequest.employee?.extra?.works?.map((work: any, idx: number) => {
                    const startMonth = thaiMonths[work.start_month] || "-";
                    const startYear = work.start_year || "-";
                    const endMonth = thaiMonths[work.end_month] || "-";
                    const endYear = work.end_year || "-";

                    const period = `${startMonth} ${startYear} - ${endMonth} ${endYear}`;

                    return (
                      <div key={idx} style={{ display: "flex", position: "relative" }}>
                        <div style={{ marginLeft: "10px", paddingRight: "5px" }}>
                          <div style={{ fontWeight: "bold" }}>ลำดับที่ {idx + 1}</div>
                        </div>
                        <div
                          style={{
                            width: "40px",
                            display: "flex",
                            justifyContent: "center",
                            position: "relative",
                          }}
                        >
                          <div
                            style={{
                              position: "absolute",
                              top: 0,
                              bottom: 0,
                              width: "2px",
                              borderLeft: `3px solid ${COLOR.primary}`,
                              marginBottom: "10px",
                            }}
                          ></div>
                        </div>
                        <div style={{ flex: 1, paddingLeft: "5px" }}>
                          <div style={{ display: "flex", marginBottom: "0.3rem" }}>
                            <strong style={{ width: "90px" }}>ช่วงเวลา</strong>
                            <div>{period || "-"}</div>
                          </div>
                          <div style={{ display: "flex", marginBottom: "1rem" }}>
                            <strong style={{ width: "90px" }}>ชื่อบริษัท</strong>
                            <div>{work.company || "-"}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ minHeight: "200px" }}>
                  <h4 style={{ color: COLOR.primary, marginTop: "2rem" }}>ข้อมูลธนาคาร</h4>
                  <div style={{ display: "flex", position: "relative" }}>
                    <div style={{ flex: 1, paddingLeft: "5px" }}>
                      <div style={{ display: "flex", marginBottom: "0.3rem" }}>
                        <strong style={{ width: "180px" }}>ชื่อ-นามสกุล</strong>
                        <div>{selectedRequest.employee?.bank || "-"}</div>
                      </div>
                      <div style={{ display: "flex", marginBottom: "0.3rem" }}>
                        <strong style={{ width: "180px" }}>เลขที่บัญชี</strong>
                        <div>{selectedRequest.employee?.bank_account_no || "-"}</div>
                      </div>
                      <div style={{ display: "flex", marginBottom: "0.3rem" }}>
                        <strong style={{ width: "180px" }}>ชื่อธนาคาร</strong>
                        <div>{selectedRequest.employee?.bank_branch || "-"}</div>
                      </div>
                      <div style={{ display: "flex", marginBottom: "0.3rem" }}>
                        <div style={{ width: "180px", fontWeight: "bold" }}>
                          อัพโหลดรูปถ่ายสำเนาบัญชีธนาคาร
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-start",
                            flex: 1,
                          }}
                        >
                          {Array.isArray(props.employeeDocuments) &&
                          props.employeeDocuments.some((d: any) => d.type === "BANK") ? (
                            <div
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                color: COLOR.primary,
                                fontWeight: "bold",
                                borderBottom: `2px solid ${COLOR.primary}`,
                                cursor: "pointer",
                                marginRight: "5px",
                              }}
                              onClick={() => handlePreviewDocument("BANK")}
                            >
                              <img
                                src={ICON.IconBankPreview}
                                alt="Bank Preview"
                                style={{ width: "14px", height: "14px", marginRight: "5px" }}
                              />
                              กดเพื่อดูรูปถ่าย
                            </div>
                          ) : (
                            <span>ไม่ได้แนบสำเนา</span>
                          )}
                        </div>
                      </div>
                      <Modal
                        open={previewOpen}
                        onClose={() => {
                          setPreviewOpen(false);
                          setZoom(1);
                        }}
                        size="small"
                      >
                        <Modal.Content>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "right",
                              alignItems: "center",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                gap: "0.2rem",
                                backgroundColor: "#e0e0e0",
                                padding: "0.1rem",
                                borderRadius: "8px",
                                alignItems: "center",
                              }}
                            >
                              <Button
                                icon
                                onClick={handleZoomOut}
                                size="tiny"
                                style={{
                                  backgroundColor: "white",
                                  borderRadius: "50%",
                                  padding: "0.1rem",
                                }}
                              >
                                <Icon name="minus" />
                              </Button>
                              <Button
                                icon
                                onClick={handleZoomIn}
                                size="tiny"
                                style={{
                                  backgroundColor: "white",
                                  borderRadius: "50%",
                                  padding: "0.1rem",
                                }}
                              >
                                <Icon name="plus" />
                              </Button>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  justifyContent: "center",
                                  paddingBottom: "0.2rem",
                                }}
                              >
                                <Icon name="zoom" />
                              </div>
                            </div>

                            <Button
                              icon
                              onClick={() => {
                                setPreviewOpen(false);
                                setZoom(1);
                              }}
                              size="large"
                              style={{
                                backgroundColor: "white",
                                color: "black",
                              }}
                            >
                              <Icon name="close" />
                            </Button>
                          </div>
                          {previewUrl ? (
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                height: "70vh",
                                overflow: "auto",
                              }}
                            >
                              <img
                                src={previewUrl}
                                alt="document preview"
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: "100%",
                                  objectFit: "contain",
                                  transform: `scale(${zoom})`,
                                  transformOrigin: "top left",
                                  transition: "transform 0.2s",
                                }}
                              />
                            </div>
                          ) : (
                            <p>เอกสารไม่พบ</p>
                          )}
                        </Modal.Content>
                      </Modal>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        ),
      },
      {
        label: "ราคาการให้บริการ",
        content: (
          <>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginTop: "40px",
                marginLeft: "20px",
                marginRight: "20px",
                gap: "2rem",
              }}
            >
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                <h3 style={{ color: COLOR.primary, fontWeight: "bold", margin: 0 }}>
                  Location การให้บริการ
                </h3>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: COLOR.light_pink,
                    borderRadius: "8px",
                    padding: "1rem",
                    fontSize: "1.1rem",
                    width: "75%",
                  }}
                >
                  {props.doctorTherapyDetail?.location_text || "-"}
                </div>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <h3 style={{ color: COLOR.primary, fontWeight: "bold", margin: 0 }}>
                  รัศมีการให้บริการ
                </h3>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: COLOR.light_pink,
                    borderRadius: "8px",
                    padding: "0.4rem 0.8rem",
                    fontSize: "1.1rem",
                    minWidth: "70px",
                  }}
                >
                  {props.doctorTherapyDetail?.service_radius || "-"}
                </div>
                <span style={{ fontSize: "1.28571429rem" }}>กิโลเมตร</span>
              </div>
            </div>

            <h3
              style={{
                color: COLOR.primary,
                marginBottom: "1rem",
                fontWeight: "bold",
                marginLeft: "20px",
              }}
            >
              รายละเอียดการให้บริการ
            </h3>
            {props.doctorTherapyDetail?.items?.map((item: any) => {
              const matchedDetail = doctorTherapyItemDetailList.find(
                (detail) => detail.id === item.id
              );
              const isActive = matchedDetail?.is_active ?? false;
              const equipments = matchedDetail?.equipments ?? [];

              return (
                <div
                  key={item.id}
                  style={{
                    border: `1px solid ${COLOR.primary}`,
                    borderRadius: "16px",
                    marginBottom: "50px",
                    marginLeft: "20px",
                    marginRight: "20px",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                    display: "flex",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                  }}
                >
                  <div
                    style={{
                      flex: "1 1 100px",
                      display: "flex",
                    }}
                  >
                    <div
                      style={{
                        position: "relative",
                        width: "100%",
                        backgroundColor: COLOR.primary,
                        overflow: "hidden",
                        borderTopLeftRadius: "16px",
                        borderBottomLeftRadius: "14px",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          bottom: 0,
                          top: 0,
                          width: "100%",
                          backgroundColor: "#fff",
                          transform: "skewY(-70deg)",
                          transformOrigin: "top right",
                          zIndex: 1,
                        }}
                      />

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          position: "relative",
                          zIndex: 2,
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        <img
                          src={ICON.IconService}
                          alt="Service Icon"
                          style={{
                            width: "56px",
                            height: "56px",
                            flexShrink: 0,
                            marginLeft: "20px",
                          }}
                        />
                        <div
                          style={{
                            fontSize: "18px",
                            fontWeight: "bold",
                            lineHeight: "1.5",
                            wordBreak: "break-word",
                            color: "#fff",
                          }}
                        >
                          {item.name || "-"}
                        </div>
                      </div>

                      <div
                        style={{
                          position: "absolute",
                          top: "-0.35rem",
                          left: "-0.4rem",
                          width: "170px",
                          height: "40px",
                          padding: "0.4rem 0.8rem",
                          backgroundImage: `url(${ICON.IconTopicLightPink})`,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                          color: "#3c3c3c",
                          borderRadius: "12px",
                          fontWeight: "bold",
                          zIndex: 3,
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <span
                          style={{
                            width: "10px",
                            height: "10px",
                            borderRadius: "50%",
                            backgroundColor: isActive ? "#66ab60" : "#e54346",
                            display: "inline-block",
                            marginLeft: "5px",
                          }}
                        />
                        {isActive ? "พร้อมให้บริการ" : "ไม่พร้อมให้บริการ"}
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      flex: "1 1 400px",
                      minWidth: "400px",
                      marginTop: "10px",
                      marginBottom: "10px",
                      marginLeft: "50px",
                      marginRight: "20px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        marginBottom: "1rem",
                        alignItems: "center",
                      }}
                    >
                      <div style={{ width: "220px", fontWeight: "bold" }}>กลุ่มอาการ</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                        {item.symptoms?.length > 0 ? (
                          item.symptoms.map((symptom: any, index: any) => (
                            <div key={index} style={lightPinkBoxStyle}>
                              {symptom}
                            </div>
                          ))
                        ) : (
                          <div>-</div>
                        )}
                      </div>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        marginBottom: "1rem",
                        justifyContent: "flex-start",
                      }}
                    >
                      <div style={{ flex: "0 0 220px", fontWeight: "bold" }}>
                        การใช้เครื่องมือร่วมกับการรักษา
                      </div>

                      {item.use_equipment ? (
                        equipments.length > 0 ? (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                            {equipments.map((eq: any, i: number) => (
                              <div key={i} style={lightPinkBoxStyle}>
                                {eq.name || eq}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div style={lightPinkBoxStyle}>มี (ไม่พบข้อมูลอุปกรณ์)</div>
                        )
                      ) : (
                        <div style={lightPinkBoxStyle}>ไม่มีการใช้อุปกรณ์</div>
                      )}
                    </div>

                    <div style={{ display: "flex", marginBottom: "1rem", alignItems: "center" }}>
                      <div style={{ width: "220px", fontWeight: "bold" }}>ประเภทการให้บริการ</div>
                      <div>
                        <div style={lightPinkBoxStyle}>
                          {item.type === "ONE_TIME" ? "รายครั้ง" : "รายชั่วโมง"}
                        </div>
                        {item.duration && (
                          <>
                            <span> ระยะเวลานาทีที่ให้บริการต่อครั้ง </span>
                            <div style={lightPinkBoxStyle}>{item.duration}</div>
                            <span> นาที</span>
                          </>
                        )}{" "}
                      </div>
                    </div>

                    <div style={{ display: "flex", alignItems: "center" }}>
                      <div style={{ width: "220px", fontWeight: "bold" }}>ราคาที่ให้บริการ</div>
                      <div>
                        {item.price && (
                          <>
                            <div style={lightPinkBoxStyle}>
                              {parseFloat(item.price).toLocaleString()}
                            </div>
                            <span> บาท</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        ),
      },
      {
        label: "ตารางการลงตรวจ",
        content: (
          <>
            <div
              style={{ height: "90vh", padding: "1rem", marginTop: "20px", marginBottom: "50px" }}
            >
              <Calendar
                localizer={localizer}
                events={events}
                defaultView="month"
                views={["day", "week", "month"]}
                step={30}
                timeslots={2}
                style={{ height: "85vh" }}
                eventPropGetter={() => ({
                  style: {
                    backgroundColor: "pink",
                    color: "#000",
                    borderRadius: "6px",
                  },
                })}
                components={{
                  event: ({ event }) => (
                    <div style={{ fontSize: "0.8em" }}>
                      {format(event.start, "HH:mm")} - {format(event.end, "HH:mm")}
                    </div>
                  ),
                }}
                onSelectEvent={(event, e) => {
                  e.preventDefault();
                  setSelectedSlot(event.resource);
                }}
                selectable
              />
            </div>
          </>
        ),
      },
    ];

    return (
      <div className="card-physio-membership-list" style={{ padding: "20px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: 20,
            gap: "10px",
          }}
        >
          <Icon
            name="chevron left"
            size="large"
            style={{ cursor: "pointer", color: COLOR.primary }}
            onClick={() => {
              props.onEvent({
                message: "HandleGetTherapistList",
              });
              setSelectedRequest(null);
              setFilterName("");
              setFilterStartDate(today);
              setFilterEndDate(today);
              setFilterStatus("");
              setActiveIndex(0);
            }}
          />
          <Header style={{ color: "rgb(79,79,79)", margin: 0 }} as="h2">
            รายชื่อนักกายภาพผู้ให้บริการใน MeHealth
          </Header>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            margin: "20px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
            <div
              style={{
                width: 100,
                height: 100,
                borderRadius: "50%",
                background: COLOR.light_gray,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                fontSize: "3rem",
                color: "#bbb",
              }}
            >
              <img
                src={
                  selectedRequest?.employee?.extra?.image
                    ? selectedRequest.employee.extra.image
                    : ICON.IconUser
                }
                style={{ width: "100px", height: "100px", borderRadius: "50%" }}
              />
            </div>

            <div style={{ marginBottom: "16px" }}>
              <div style={{ display: "flex", alignItems: "center", marginBottom: "4px" }}>
                <p style={{ fontWeight: "bold", minWidth: "200px", margin: 0 }}>ชื่อ-นามสกุล</p>
                <span style={{ fontWeight: "normal" }}>
                  {getDisplayName(selectedRequest.employee)}
                </span>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "4px",
                  marginTop: "8px",
                }}
              >
                <p style={{ fontWeight: "bold", minWidth: "200px", margin: 0 }}>
                  เลขที่ใบประกอบวิชาชีพ
                </p>
                <span style={{ fontWeight: "normal" }}>{selectedRequest.employee?.licence}</span>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  marginTop: "8px",
                  marginBottom: "4px",
                }}
              >
                <p style={{ fontWeight: "bold", margin: 0, minWidth: "190px" }}>สถานะปัจจุบัน</p>
                <div
                  style={{
                    display: "inline-block",
                    padding: "5px 15px",
                    borderRadius: "4px",
                    background: selectedRequest.status === "A" ? "#c4eac5" : COLOR.light_gray,
                    fontWeight: "bold",
                    fontSize: "1.1rem",
                    color: selectedRequest.status === "A" ? "#137e45" : "black",
                  }}
                >
                  {selectedRequest.status === "A"
                    ? "เปิดให้บริการ"
                    : selectedRequest.status === "SU"
                    ? "ถูกระงับชั่วคราว"
                    : "ยกเลิกให้บริการ"}
                </div>
              </div>
            </div>
          </div>

          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <button
              style={{
                padding: "6px 12px",
                backgroundColor: "white",
                color: COLOR.dark_orange,
                borderRadius: "4px",
                cursor: "pointer",
                fontWeight: "bold",
                border: `1px solid ${COLOR.dark_orange}`,
              }}
            >
              ระงับชั่วคราว
            </button>
            <button
              style={{
                padding: "6px 12px",
                backgroundColor: "white",
                color: COLOR.gray,
                borderRadius: "4px",
                cursor: "pointer",
                fontWeight: "bold",
                border: `1px solid ${COLOR.gray}`,
              }}
            >
              ยกเลิกการให้บริการ
            </button>
          </div>

          <Modal
            open={isRejectReasonModalOpen}
            onClose={() => setIsRejectReasonModalOpen(false)}
            size="small"
          >
            <Modal.Content>
              <Icon
                name="close"
                style={{ position: "absolute", top: "20px", right: "10px", cursor: "pointer" }}
                onClick={() => setIsRejectReasonModalOpen(false)}
                size="large"
              />
              <h3 style={{ display: "flex", alignItems: "center" }}>
                <img
                  src={ICON.IconReject}
                  alt="Reject"
                  style={{
                    width: "20px",
                    height: "20px",
                    marginRight: "5px",
                    verticalAlign: "middle",
                  }}
                />
                สาเหตุที่ถูกปฏิเสธการสมัคร
              </h3>

              <Table celled style={{ marginBottom: "20px", maxHeight: "300px", overflowY: "auto" }}>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell style={{ backgroundColor: "#ddd" }}>
                      วันที่ได้รับการปฏิเสธ
                    </Table.HeaderCell>
                    <Table.HeaderCell style={{ backgroundColor: COLOR.primary, color: "white" }}>
                      สาเหตุที่ถูกปฏิเสธ
                    </Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {(selectedRequest.actions || [])
                    .filter((a: any) => a.action === "REJECT")
                    .map((a: any) => (
                      <Table.Row key={a.id}>
                        <Table.Cell>{moment(a.datetime).format("DD/MM/YYYY HH.mm")} น.</Table.Cell>
                        <Table.Cell>{a.comment || "-"}</Table.Cell>
                      </Table.Row>
                    ))}
                </Table.Body>
              </Table>
            </Modal.Content>
          </Modal>

          <Modal open={isEditHistoryOpen} onClose={() => setIsEditHistoryOpen(false)} size="large">
            <Modal.Content scrolling>
              <Icon
                name="close"
                style={{ position: "absolute", top: "20px", right: "10px", cursor: "pointer" }}
                onClick={() => setIsEditHistoryOpen(false)}
                size="large"
              />
              <h3 style={{ display: "flex", alignItems: "center" }}>ประวัติการแก้ไขข้อมูล</h3>

              {(() => {
                const filteredLogs = (props.auditLogList || [])
                  .filter((log: any) => {
                    if (log.edit_user?.id !== selectedRequest.owner || log.action !== "UPDATE")
                      return false;

                    const diffValues = log.diff?.values_changed || {};
                    const editedChange = diffValues["root['edited']"];
                    if (editedChange) {
                      const oldEdited = new Date(editedChange.old_value);
                      const newEdited = new Date(editedChange.new_value);
                      const diffMs = Math.abs(newEdited.getTime() - oldEdited.getTime());
                      const diffMinutes = diffMs / 1000 / 60;
                      if (diffMinutes < 1) return false;
                    }

                    const hasRealOldValue = Object.values(diffValues).some(
                      (change: any) =>
                        change?.old_value !== "" &&
                        change?.old_value !== null &&
                        change?.old_value !== undefined
                    );
                    return (
                      hasRealOldValue ||
                      Object.keys(log.diff?.iterable_item_added || {}).length > 0 ||
                      Object.keys(log.diff?.iterable_item_removed || {}).length > 0
                    );
                  })
                  .sort(
                    (a: any, b: any) =>
                      new Date(b.created).getTime() - new Date(a.created).getTime()
                  );

                if (filteredLogs.length === 0) {
                  return (
                    <div
                      style={{
                        backgroundColor: COLOR.light_pink,
                        border: "1px solid #ffb3c6",
                        borderRadius: "8px",
                        padding: "15px",
                        textAlign: "center",
                        color: COLOR.primary,
                        fontWeight: "bold",
                        marginTop: "10px",
                      }}
                    >
                      ไม่พบการแก้ไขข้อมูล
                    </div>
                  );
                }

                return filteredLogs.map((log: any) => {
                  const diffData = {
                    ...(log.diff?.values_changed || {}),
                    ...(log.diff?.type_changes || {}),
                    ...(log.diff?.iterable_item_added || {}),
                    ...(log.diff?.iterable_item_removed || {}),
                  };
                  const diffValues = log.diff?.values_changed || {};
                  const changedKeys = Object.keys(diffValues).filter((k) => k !== "root['edited']");
                  const extraValues = log.diff?.values_changed || {};
                  const iterableAdded = log.diff?.iterable_item_added || {};
                  const iterableRemoved = log.diff?.iterable_item_removed || {};
                  const extraRows: any[] = [];

                  const getSpecialtyName = (code: string) =>
                    props.specialtyList?.find((s: any) => s.code === code)?.name || code;

                  Object.keys(extraValues)
                    .filter((key) => key.startsWith("root['extra']['specialties']"))
                    .forEach((key) => {
                      const val = extraValues[key].new_value ?? "-";
                      const indexMatch = key.match(/\[(\d+)\]/);
                      const order = indexMatch ? Number(indexMatch[1]) + 1 : "-";
                      extraRows.push({
                        main: "ข้อมูลทางวิชาชีพ",
                        sub: `(ลำดับที่ ${order}) ความเชี่ยวชาญและกลุ่มอาการ`,
                        value: getSpecialtyName(val),
                      });
                    });

                  Object.entries(iterableAdded)
                    .filter(([path, val]: any) => path.startsWith("root['extra']['specialties']"))
                    .forEach(([path, val]: any) => {
                      const indexMatch = path.match(/\[(\d+)\]/);
                      const order = indexMatch ? Number(indexMatch[1]) + 1 : "-";
                      extraRows.push({
                        main: "ข้อมูลทางวิชาชีพ (เพิ่มข้อมูล)",
                        sub: `(ลำดับที่ ${order}) เพิ่มความเชี่ยวชาญและกลุ่มอาการ`,
                        value: getSpecialtyName(val),
                      });
                    });

                  Object.entries(iterableRemoved)
                    .filter(([path, val]: any) => path.startsWith("root['extra']['specialties']"))
                    .forEach(([path, val]: any) => {
                      const indexMatch = path.match(/\[(\d+)\]/);
                      const order = indexMatch ? Number(indexMatch[1]) + 1 : "-";
                      extraRows.push({
                        main: "ข้อมูลทางวิชาชีพ (ลบข้อมูล)",
                        sub: `(ลำดับที่ ${order}) ลบความเชี่ยวชาญและกลุ่มอาการ`,
                        value: getSpecialtyName(val),
                      });
                    });

                  if (extraValues["root['extra']['work_address']"]) {
                    const val = extraValues["root['extra']['work_address']"].new_value ?? "-";
                    extraRows.push({
                      main: "ข้อมูลทางวิชาชีพ",
                      sub: "สถานที่ทำงาน",
                      value: val,
                    });
                  }

                  const hasRealChange =
                    changedKeys.length > 0 ||
                    Object.keys(iterableAdded).length > 0 ||
                    Object.keys(iterableRemoved).length > 0;

                  if (!hasRealChange) return false;

                  const filteredEntries = Object.entries(diffData).filter(([path]) => {
                    const fieldName = path.replace(/root\['|'\]/g, "");
                    return (
                      !EXCLUDE_FIELDS.includes(fieldName) &&
                      !(iterableAdded[path] || iterableRemoved[path])
                    );
                  });

                  const educationDiffs = Object.entries(diffData).filter(([path]) =>
                    path.startsWith("root['extra']['educations']")
                  );
                  const workDiffs = Object.entries(diffData).filter(([path]) =>
                    path.startsWith("root['extra']['works']")
                  );

                  const getIndexes = (diffs: any[], type: "educations" | "works") => {
                    const regex = new RegExp(`root\\['extra'\\]\\['${type}'\\]\\[(\\d+)\\]`);
                    return [
                      ...new Set(
                        diffs
                          .map(([path]) => {
                            const match = path.match(regex);
                            return match ? Number(match[1]) : null;
                          })
                          .filter((x) => x !== null)
                      ),
                    ];
                  };

                  const buildRows = (
                    items: any[],
                    type: "education" | "work",
                    indexes: number[],
                    actionText: string = ""
                  ) => {
                    return items.flatMap((item, idx) => {
                      const rows: any[] = [];
                      const prefix = type === "education" ? "ประวัติการศึกษา" : "ประวัติการทำงาน";
                      const order = indexes[idx] + 1;

                      const mainPrefix = actionText ? `${prefix} (${actionText})` : prefix;

                      if (type === "education") {
                        if (item.category)
                          rows.push({
                            main: mainPrefix,
                            sub: `(ลำดับที่ ${order}) หมวดการศึกษา`,
                            value: getCategoryName(item.category),
                          });
                        if (item.degree_name)
                          rows.push({
                            main: mainPrefix,
                            sub: `(ลำดับที่ ${order}) ชื่อปริญญา`,
                            value: item.degree_name,
                          });
                        if (item.faculty)
                          rows.push({
                            main: mainPrefix,
                            sub: `(ลำดับที่ ${order}) คณะ`,
                            value: item.faculty,
                          });
                        if (item.graduate_year)
                          rows.push({
                            main: mainPrefix,
                            sub: `(ลำดับที่ ${order}) ปีที่สำเร็จการศึกษา`,
                            value: item.graduate_year,
                          });
                        if (item.major)
                          rows.push({
                            main: mainPrefix,
                            sub: `(ลำดับที่ ${order}) สาขา`,
                            value: item.major,
                          });
                        if (item.school)
                          rows.push({
                            main: mainPrefix,
                            sub: `(ลำดับที่ ${order}) สถาบัน`,
                            value: getSchoolName(item.school),
                          });
                        if (item.level)
                          rows.push({
                            main: mainPrefix,
                            sub: `(ลำดับที่ ${order}) ระดับ`,
                            value: getEducationLevelName(item.level),
                          });
                        if (item.status)
                          rows.push({
                            main: mainPrefix,
                            sub: `(ลำดับที่ ${order}) สถานภาพการศึกษา`,
                            value: getEducationStatusName(item.status),
                          });
                      } else if (type === "work") {
                        const startMonth = thaiMonths[item.start_month] || "-";
                        const startYear = item.start_year || "-";
                        const endMonth = thaiMonths[item.end_month] || "-";
                        const endYear = item.end_year || "-";
                        if (item.start_month && item.start_year)
                          rows.push({
                            main: mainPrefix,
                            sub: `(ลำดับที่ ${order}) เริ่มทำงานตั้งแต่`,
                            value: `${startMonth} ${startYear}`,
                          });
                        if (item.end_month && item.end_year)
                          rows.push({
                            main: mainPrefix,
                            sub: `(ลำดับที่ ${order}) ทำงานถึง`,
                            value: `${endMonth} ${endYear}`,
                          });
                        if (item.company)
                          rows.push({
                            main: mainPrefix,
                            sub: `(ลำดับที่ ${order}) ชื่อบริษัท`,
                            value: item.company,
                          });
                        if (item.address)
                          rows.push({
                            main: mainPrefix,
                            sub: `(ลำดับที่ ${order}) ที่อยู่`,
                            value: item.address,
                          });
                        if (item.level)
                          rows.push({
                            main: mainPrefix,
                            sub: `(ลำดับที่ ${order}) ระดับ`,
                            value: getJobLevelText(item.level),
                          });
                        if (item.type)
                          rows.push({
                            main: mainPrefix,
                            sub: `(ลำดับที่ ${order}) ประเภทงาน`,
                            value: getJobTypeText(item.type),
                          });
                        if (item.position)
                          rows.push({
                            main: mainPrefix,
                            sub: `(ลำดับที่ ${order}) ตำแหน่งงาน`,
                            value: item.position,
                          });
                        if (item.description)
                          rows.push({
                            main: mainPrefix,
                            sub: `(ลำดับที่ ${order}) รายละเอียดงาน`,
                            value: item.description,
                          });
                      }
                      return rows;
                    });
                  };

                  const eduIndexes = getIndexes(educationDiffs, "educations");
                  const workIndexes = getIndexes(workDiffs, "works");

                  const educationRows = buildRows(
                    eduIndexes.map((i) => {
                      const obj: any = {};
                      educationDiffs
                        .filter(([p]) => p.includes(`[${i}]`))
                        .forEach(([path, val]: any) => {
                          const key = path.split("['").pop()?.replace("']", "");
                          obj[key!] = val.new_value;
                        });
                      return obj;
                    }),
                    "education",
                    eduIndexes
                  );

                  const educationAddedRows = buildRows(
                    Object.values(iterableAdded).filter((path: any) => path.school !== undefined),
                    "education",
                    Object.keys(iterableAdded).map((k) => Number(k.match(/\[(\d+)\]/)?.[1] ?? 0)),
                    "เพิ่มข้อมูล"
                  );

                  const educationRemovedRows = buildRows(
                    Object.values(iterableRemoved).filter((path: any) => path.school !== undefined),
                    "education",
                    Object.keys(iterableRemoved).map((k) => Number(k.match(/\[(\d+)\]/)?.[1] ?? 0)),
                    "ลบข้อมูล"
                  );

                  const allEducationRows = [
                    ...educationRows,
                    ...educationAddedRows,
                    ...educationRemovedRows,
                  ];

                  const workRows = buildRows(
                    workIndexes.map((i) => {
                      const obj: any = {};
                      workDiffs
                        .filter(([p]) => p.includes(`[${i}]`))
                        .forEach(([path, val]: any) => {
                          const key = path.split("['").pop()?.replace("']", "");
                          obj[key!] = val.new_value;
                        });
                      return obj;
                    }),
                    "work",
                    workIndexes
                  );

                  const workAddedRows = buildRows(
                    Object.values(iterableAdded).filter((path: any) => path.company !== undefined),
                    "work",
                    Object.keys(iterableAdded).map((k) => Number(k.match(/\[(\d+)\]/)?.[1] ?? 0)),
                    "เพิ่มข้อมูล"
                  );

                  const workRemovedRows = buildRows(
                    Object.values(iterableRemoved).filter(
                      (path: any) => path.company !== undefined
                    ),
                    "work",
                    Object.keys(iterableRemoved).map((k) => Number(k.match(/\[(\d+)\]/)?.[1] ?? 0)),
                    "ลบข้อมูล"
                  );

                  const allWorkRows = [...workRows, ...workAddedRows, ...workRemovedRows];

                  return (
                    <div
                      key={log.id}
                      style={{
                        border: "1px solid #ddd",
                        borderRadius: "8px",
                        padding: "10px",
                        marginBottom: "10px",
                      }}
                    >
                      <div
                        style={{
                          marginBottom: "5px",
                          fontWeight: "bold",
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <div>
                          <img
                            src={ICON.IconEdit}
                            alt="Edit"
                            style={{
                              width: "20px",
                              height: "20px",
                              marginRight: "5px",
                              verticalAlign: "middle",
                            }}
                          />
                          ส่งข้อมูลเข้ามาตรวจสอบใหม่
                        </div>
                        <div>
                          วันที่ส่งตรวจสอบ
                          <span style={{ color: COLOR.primary, marginLeft: "5px" }}>
                            {moment(log.created).format("DD/MM/YYYY HH:mm")} น.
                          </span>
                        </div>
                      </div>

                      <Table celled compact>
                        <Table.Header style={{ textAlign: "center" }}>
                          <Table.Row>
                            <Table.HeaderCell>หัวข้อหลักของข้อมูล</Table.HeaderCell>
                            <Table.HeaderCell>ส่วนที่แก้ไข</Table.HeaderCell>
                            <Table.HeaderCell
                              style={{ backgroundColor: COLOR.primary, color: "white" }}
                            >
                              ข้อมูลที่แก้ไข
                            </Table.HeaderCell>
                          </Table.Row>
                        </Table.Header>
                        <Table.Body>
                          {filteredEntries.map(([fieldPath, value]: any, index: number) => {
                            const fieldName = fieldPath.replace(/root\['|'\]/g, "");
                            const mapping = EDIT_DATA_MAPPING[fieldName];
                            if (!mapping) return null;

                            let newVal = value.new_value ?? "-";

                            if (fieldName === "birth_date" && newVal) {
                              newVal = moment(newVal).format("DD/MM/YYYY");
                            }
                            if (fieldName === "gender" && newVal) {
                              newVal = newVal === "M" ? "ชาย" : newVal === "F" ? "หญิง" : newVal;
                            }

                            return (
                              <Table.Row key={`main-${index}`}>
                                <Table.Cell>{mapping.main}</Table.Cell>
                                <Table.Cell>{mapping.sub}</Table.Cell>
                                <Table.Cell>{newVal.toString()}</Table.Cell>
                              </Table.Row>
                            );
                          })}

                          {extraRows.map((row, i) => (
                            <Table.Row key={`extra-${i}`}>
                              <Table.Cell>{row.main}</Table.Cell>
                              <Table.Cell>{row.sub}</Table.Cell>
                              <Table.Cell>{row.value}</Table.Cell>
                            </Table.Row>
                          ))}

                          {allEducationRows.map((row: any, i: any) => (
                            <Table.Row key={`edu-${i}`}>
                              <Table.Cell>{row.main}</Table.Cell>
                              <Table.Cell>{row.sub}</Table.Cell>
                              <Table.Cell>{row.value}</Table.Cell>
                            </Table.Row>
                          ))}

                          {allWorkRows.map((row: any, i: any) => (
                            <Table.Row key={`work-${i}`}>
                              <Table.Cell>{row.main}</Table.Cell>
                              <Table.Cell>{row.sub}</Table.Cell>
                              <Table.Cell>{row.value}</Table.Cell>
                            </Table.Row>
                          ))}
                        </Table.Body>
                      </Table>
                    </div>
                  );
                });
              })()}
            </Modal.Content>
          </Modal>
        </div>
        <CustomTabs
          tabs={panes}
          onToggleEditHistory={() => setIsEditHistoryOpen((prev) => !prev)}
        />
        <Modal open={!!selectedSlot} onClose={() => setSelectedSlot(null)} size="small">
          {selectedSlot && (
            <>
              <Modal.Content>
                <Button
                  icon
                  onClick={() => setSelectedSlot(null)}
                  size="large"
                  style={{
                    position: "absolute",
                    top: "10px",
                    right: "10px",
                    backgroundColor: "white",
                    color: "black",
                    borderRadius: "50%",
                    padding: "10px",
                    minWidth: "0",
                  }}
                >
                  <Icon name="close" />
                </Button>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "40px",
                    fontWeight: "bold",
                    fontSize: "20px",
                    marginTop: "20px",
                    marginBottom: "40px",
                    marginLeft: "20px",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <Icon
                      name="calendar"
                      style={{
                        color: COLOR.primary,
                        display: "flex",
                        alignItems: "center",
                        marginTop: "-5px",
                      }}
                    />
                    <span>{formatThaiDate(selectedSlot.date)}</span>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <Icon
                      name="clock"
                      style={{
                        color: COLOR.primary,
                        display: "flex",
                        alignItems: "center",
                        marginTop: "-5px",
                      }}
                    />
                    <span>
                      {selectedSlot.start_time} - {selectedSlot.end_time}
                    </span>
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "15px",
                    fontSize: "18px",
                    marginLeft: "60px",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    เวลาเริ่มต้น
                    <input
                      type="text"
                      value={selectedSlot.start_time}
                      disabled
                      style={{
                        width: "80px",
                        padding: "2px 5px",
                        textAlign: "center",
                        borderRadius: "8px",
                      }}
                    />
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    เวลาสิ้นสุด
                    <input
                      type="text"
                      value={selectedSlot.end_time}
                      disabled
                      style={{
                        width: "80px",
                        padding: "2px 5px",
                        textAlign: "center",
                        borderRadius: "8px",
                      }}
                    />
                  </div>

                  <label style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    <input type="checkbox" checked={selectedSlot.blocked || false} />
                    งดให้บริการ
                  </label>
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    marginTop: "20px",
                    marginBottom: "40px",
                    fontSize: "18px",
                    marginLeft: "60px",
                  }}
                >
                  เวลาในการให้บริการแต่ละเคสโดยประมาณ
                  <input
                    type="text"
                    value={
                      selectedSlot.providers?.[0]?.dsb?.appointments?.[0]?.estimated_duration ?? "-"
                    }
                    disabled
                    style={{
                      width: "100px",
                      padding: "2px 5px",
                      textAlign: "center",
                      borderRadius: "8px",
                    }}
                  />
                  นาที
                </div>
              </Modal.Content>
            </>
          )}
        </Modal>
      </div>
    );
  };

  const renderTherapistListTable = (data: any[], setSelectedRequest: any, emptyRows: any[]) => (
    <Table celled structured selectable style={{ textAlign: "center", marginLeft: "15px" }}>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>รายชื่อผู้ที่ได้รับการอนุมัติ</Table.HeaderCell>
          <Table.HeaderCell>วันที่ได้รับการอนุมัติ</Table.HeaderCell>
          <Table.HeaderCell>สถานะปัจจุบัน</Table.HeaderCell>
          <Table.HeaderCell>การตรวจสอบ</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {data.map((therapist: any) => (
          <Table.Row key={therapist.id}>
            <Table.Cell style={{ textAlign: "left" }}>
              {(() => {
                const name =
                  therapist.employee.full_name?.trim() ||
                  `${therapist.employee.first_name ?? ""} ${
                    therapist.employee.last_name ?? ""
                  }`.trim();
                const licence = therapist.employee.licence?.trim();
                return licence ? `${name} (${licence})` : name;
              })()}
            </Table.Cell>
            <Table.Cell>{moment(therapist.approve_at).format("DD/MM/YYYY")}</Table.Cell>
            <Table.Cell>
              {therapist.status === "A"
                ? "เปิดให้บริการ"
                : therapist.status === "SU"
                ? "ถูกระงับชั่วคราว"
                : "ยกเลิกให้บริการ"}
            </Table.Cell>
            <Table.Cell>
              <Button
                size="small"
                style={{
                  border: `1px solid ${COLOR.primary}`,
                  color: COLOR.primary,
                  backgroundColor: "white",
                }}
                onClick={() => setSelectedRequest(therapist)}
              >
                ตรวจสอบข้อมูล
              </Button>
            </Table.Cell>
          </Table.Row>
        ))}
        {emptyRows.map((_, idx) => (
          <Table.Row key={`empty-${idx}`} style={{ height: "50px" }}>
            <Table.Cell>&nbsp;</Table.Cell>
            <Table.Cell>&nbsp;</Table.Cell>
            <Table.Cell>&nbsp;</Table.Cell>
            <Table.Cell>&nbsp;</Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  );

  const matchedDoctor = useMemo(() => {
    if (!props.doctorList || !selectedRequest?.employee?.id) return null;

    return props.doctorList.find((doc: any) => doc.employee === selectedRequest.employee.id);
  }, [props.doctorList, selectedRequest]);

  const matchedTherapyItemDetails = useMemo(() => {
    if (!props.doctorTherapyDetail?.items?.length || !doctorTherapyItemDetailList.length) {
      return [];
    }

    return props.doctorTherapyDetail.items.filter((item: any) =>
      doctorTherapyItemDetailList.some((detail: any) => detail.id === item.id)
    );
  }, [props.doctorTherapyDetail?.items, doctorTherapyItemDetailList]);

  const providerId = useMemo(() => {
    if (!matchedDoctor?.provider?.length) return null;

    return matchedDoctor.provider[0]?.doctor_provider_id || null;
  }, [matchedDoctor]);

  const events = useMemo(() => {
    if (!props.schedulingList?.length) return [];

    return props.schedulingList.map((item) => {
      const start = moment(`${item.date} ${item.start_time}`, "YYYY-MM-DD HH:mm").toDate();
      const end = moment(`${item.date} ${item.end_time}`, "YYYY-MM-DD HH:mm").toDate();

      return {
        id: item.dsb_id,
        title: `${item.division_name} (${item.block_serial})`,
        start,
        end,
        resource: item,
      };
    });
  }, [props.schedulingList]);

  useEffect(() => {
    props.onEvent({
      message: "HandleGetTherapistList",
    });
    props.onEvent({
      message: "HandleGetEducationList",
    });
    props.onEvent({
      message: "HandleGetSpecialtyList",
    });
    props.onEvent({
      message: "HandleGetClinicalTermSet",
    });
    props.onEvent({
      message: "HandleGetDoctorList",
    });
    props.onEvent({
      message: "HandleGetDoctorTherapyList",
    });
    props.onEvent({
      message: "HandleGetAuditLogList",
      params: { model: "employee" },
    });
  }, []);

  useEffect(() => {
    if (!matchedDoctor?.id) return;

    props.onEvent({
      message: "HandleGetDoctorTherapyDetail",
      params: { doctor_id: matchedDoctor.id },
    });
  }, [matchedDoctor]);

  useEffect(() => {
    if (!matchedDoctor?.id || !props.doctorTherapyDetail?.items?.length) return;

    props.doctorTherapyDetail.items.forEach((item: any) => {
      props.onEvent({
        message: "HandleGetDoctorTherapyItemDetail",
        params: { doctor_id: matchedDoctor.id, pk: item.id },
      });
    });
  }, [matchedDoctor, props.doctorTherapyDetail?.items]);

  useEffect(() => {
    if (!providerId) return;

    props.onEvent({
      message: "HandleGetScheduling",
      params: {
        providerId: providerId,
        start_date: "2000-01-01",
        end_date: "2100-12-29",
      },
    });
  }, [providerId]);

  useEffect(() => {
    if (props.doctorTherapyItemDetail && props.doctorTherapyItemDetail.id) {
      setDoctorTherapyItemDetailList((prev) => {
        const exists = prev.some((item) => item.id === props.doctorTherapyItemDetail.id);
        if (exists) return prev;
        return [...prev, props.doctorTherapyItemDetail];
      });
    }
  }, [props.doctorTherapyItemDetail]);

  useEffect(() => {
    if (selectedRequest?.employee?.id) {
      props.onEvent({
        message: "HandleGetEmployeeDocuments",
        params: { employee_id: selectedRequest.employee?.id },
      });
    }
  }, [selectedRequest?.employee?.id]);

  useEffect(() => {
    const initialData = (props.currentTherapists || []).filter((t: any) => t.status === "A");
    setFilteredTherapists(initialData);
  }, [props.currentTherapists]);

  useEffect(() => {
    props.onEvent({
      message: "HandleGetTherapistList",
    });
  }, [props.currentTherapists?.status]);

  console.log("employeeDocuments", props.employeeDocuments);

  if (selectedRequest) {
    return renderPersonalDetail(selectedRequest, setSelectedRequest);
  }

  return (
    <div
      className="card-physio-membership-list"
      style={{
        display: "flex",
        flexDirection: "column",
        padding: "15px",
        height: "calc(100vh - 50px)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 40,
          padding: "0 15px",
        }}
      >
        <Header style={{ color: "rgb(79,79,79)", margin: 0 }} as="h2">
          รายชื่อนักกายภาพผู้ให้บริการใน MeHealth
        </Header>
      </div>

      <div
        style={{
          marginBottom: "1rem",
          display: "flex",
          justifyContent: "space-between",
          padding: "0 15px",
        }}
      >
        <div>
          <span style={{ marginRight: "1rem" }}>ชื่อ/นามสกุล</span>
          <Input
            placeholder="ระบุคำค้นหา"
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
            style={{ minWidth: "300px" }}
          />
        </div>

        <div>
          <span style={{ marginRight: "1rem" }}>วันที่</span>
          <FilterDate value={filterStartDate} onChange={setFilterStartDate} />
          <span style={{ marginRight: "1rem", marginLeft: "1rem" }}>ถึง</span>
          <FilterDate value={filterEndDate} onChange={setFilterEndDate} />
        </div>

        <div>
          <span style={{ marginRight: "1rem" }}>สถานะปัจจุบัน</span>
          <Dropdown
            search
            selection
            options={STATUS_OPTIONS}
            value={filterStatus}
            onChange={(e, { value }: any) => setFilterStatus(value)}
          />
        </div>

        <Button
          style={{ color: "white", backgroundColor: COLOR.primary }}
          loading={isSearching}
          onClick={handleSearchClick}
        >
          ค้นหา
        </Button>
      </div>

      <div
        style={{
          width: "100%",
          height: "100%",
          overflowY: "auto",
          overflowX: "hidden",
          paddingRight: "8px",
          marginBottom: "50px",
          flex: 1,
        }}
      >
        {renderTherapistListTable(currentData, setSelectedRequest, emptyRows)}
        <div style={{ display: "flex", justifyContent: "center", marginTop: "1rem" }}>
          <Pagination
            activePage={activePage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      </div>
    </div>
  );
};

export default CardPhysiotherapistList;
